import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtNum } from "@/utils/toEnglishDigits";
import { fmtDate, fmtTime, combineDateTime } from "@/utils/date";
import { X, User, UserPlus, CheckCircle, WarningCircle, ArrowLeft, WhatsappLogo, Warning, ArrowsClockwise, ArrowRight, Info, Plus } from "@phosphor-icons/react";
import { openDriverWhatsApp, buildDriverAssignmentMessage } from "@/utils/whatsappDriver";
import { formatPhoneDisplay, formatPhoneForWhatsApp } from "@/utils/phoneFormat";
import { buildCapacityMap, loadCachedCapacityMap, saveCachedCapacityMap } from "@/utils/seats";
import PhoneInput from "@/components/PhoneInput";
import NumberField from "@/components/NumberField";
import { validatePhone } from "@/utils/phoneValidation";

const TAB_FLEET = "fleet";
const TAB_EXTERNAL = "external";

// ─── Override confirmation modal ──────────────────────────────────────
function OverrideConfirm({ driver, booking, onConfirm, onCancel }) {
  if (!driver) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: "rgba(5,10,21,0.85)", backdropFilter: "blur(8px)" }}
      onClick={onCancel}
      dir="rtl"
      data-testid="override-modal"
    >
      <div
        className="bg-card border-2 border-[#F57F17]/60 rounded-2xl w-full max-w-md shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-[#F57F17]/25 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#F57F17]/15 border border-[#F57F17]/50 flex items-center justify-center">
            <Warning size={22} weight="fill" className="text-[#F57F17]" />
          </div>
          <div>
            <h3 className="text-[#F57F17] font-bold text-lg">تنبيه: السائق مشغول</h3>
            <div className="text-white/50 text-xs mt-0.5">Double-booking Override</div>
          </div>
        </div>
        <div className="p-5 space-y-4 text-sm text-white/85">
          <p className="leading-relaxed">
            السائق <span className="text-primary font-bold">{driver.name}</span> لديه حجز آخر في نفس الوقت:
          </p>
          {driver.conflict && (
            <div className="rounded-xl border border-[#F57F17]/40 bg-[#F57F17]/10 p-3 space-y-1 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-white/60">📋 رقم الحجز:</span>
                <span className="num text-primary font-bold">#{driver.conflict.booking_number}</span>
              </div>
              {driver.conflict.trip_date && (
                <div className="flex items-center gap-2">
                  <span className="text-white/60">📅 التاريخ:</span>
                  <span className="num">{driver.conflict.trip_date}</span>
                </div>
              )}
              {driver.conflict.trip_time && (
                <div className="flex items-center gap-2">
                  <span className="text-white/60">🕐 الوقت:</span>
                  <span className="num">{driver.conflict.trip_time}</span>
                </div>
              )}
            </div>
          )}
          <p className="text-white/80 leading-relaxed">
            هل أنت متأكد من إسناده لهذا الحجز أيضاً <span className="text-[#F57F17] font-bold">(رحلة إضافية)</span>؟
          </p>
        </div>
        <div className="p-4 border-t border-white/10 flex items-center gap-2 justify-end">
          <button
            type="button"
            onClick={onCancel}
            data-testid="override-cancel"
            className="px-5 py-2.5 rounded-full text-sm border border-white/20 text-white/70 hover:bg-white/5"
          >
            إلغاء
          </button>
          <button
            type="button"
            onClick={onConfirm}
            data-testid="override-confirm"
            className="px-6 py-2.5 rounded-full text-sm font-bold bg-[#F57F17] text-[#0A1936] hover:bg-[#FBBF24] inline-flex items-center gap-1.5"
          >
            <Warning size={14} weight="bold" /> نعم، أسنده
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DriverAssignModal({ booking, groupSiblings, onClose, onSaved }) {
  const [tab, setTab] = useState(TAB_FLEET);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bookingInfo, setBookingInfo] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [pickedDriverId, setPickedDriverId] = useState(null);
  const [allowDouble, setAllowDouble] = useState(false);
  const [overrideFor, setOverrideFor] = useState(null); // { driver }
  const [wage, setWage] = useState("");
  const [extName, setExtName] = useState("");
  const [extPhone, setExtPhone] = useState("");
  // External driver picker — "pick" = choose from registered list, "manual" = free text.
  const [extMode, setExtMode] = useState("pick"); // "pick" | "manual"
  const [extList, setExtList] = useState([]);
  const [extListLoading, setExtListLoading] = useState(false);
  const [extPickedId, setExtPickedId] = useState(null);
  // Siblings in the same booking_group_id. If > 1, offer bulk-assign checkbox.
  const siblings = Array.isArray(groupSiblings) ? groupSiblings : [];
  const hasGroup = siblings.length > 1;
  const [assignToGroup, setAssignToGroup] = useState(false);
  const [carsCapacity, setCarsCapacity] = useState(() => loadCachedCapacityMap());
  const [carsLabels, setCarsLabels] = useState({});
  // Wage suggestion — computed by /admin/driver-wage-rules/suggest whenever the
  // selected driver + booking route + car type change. Auto-fills the wage
  // input when it's empty (respects manual edits).
  const [suggestion, setSuggestion] = useState(null);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [wageManuallyEdited, setWageManuallyEdited] = useState(false);
  useEffect(() => {
    api.get("/cars/capacity")
      .catch(() => api.get("/cars"))
      .then((r) => {
        const m = buildCapacityMap(r.data);
        if (Object.keys(m).length > 0) { setCarsCapacity(m); saveCachedCapacityMap(m); }
        // Also build a car_type → Arabic label map so the "🔀" mismatch badge
        // in the wage card can show a friendly name instead of a slug.
        const labels = {};
        for (const c of (Array.isArray(r.data) ? r.data : [])) {
          const key = c.id || c.car_type || c.type || c.slug;
          if (key) labels[key] = c.name_ar || c.name || key;
        }
        setCarsLabels(labels);
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const routeIdx = booking?._assignRouteIdx;
      const carIdx = booking?._assignCarIdx;
      const params = new URLSearchParams();
      if (routeIdx !== undefined && routeIdx !== null) params.set("route_idx", String(routeIdx));
      if (carIdx !== undefined && carIdx !== null) params.set("car_idx", String(carIdx));
      const qs = params.toString();
      const url = qs
        ? `/admin/bookings/${booking.id}/available-drivers?${qs}`
        : `/admin/bookings/${booking.id}/available-drivers`;
      const { data } = await api.get(url);
      setBookingInfo(data.booking);
      setDrivers(data.drivers || []);
      setPickedDriverId(data.booking?.current_driver_id || null);
      setWage(String(data.booking?.current_driver_wage || ""));
      setExtName(data.booking?.current_external_driver_name || "");
      setExtPhone(data.booking?.current_external_driver_phone || "");
      setExtPickedId(data.booking?.current_external_driver_id || null);
      // If the booking already has an external driver assigned, default the
      // external tab to "manual" so its data is visible/editable.
      if (data.booking?.current_external_driver_name && !data.booking?.current_external_driver_id) {
        setExtMode("manual");
      }
    } catch {
      toast.error("تعذر جلب السائقين المتاحين");
    } finally {
      setLoading(false);
    }
  }, [booking.id, booking._assignRouteIdx, booking._assignCarIdx]);

  useEffect(() => { load(); }, [load]);

  // Lazy-load the active external drivers list the first time the external
  // tab is opened.
  useEffect(() => {
    if (tab !== TAB_EXTERNAL || extList.length > 0 || extListLoading) return;
    setExtListLoading(true);
    api.get("/admin/external-drivers")
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data.filter((d) => d.is_active !== false) : [];
        setExtList(list);
      })
      .catch(() => setExtList([]))
      .finally(() => setExtListLoading(false));
  }, [tab, extList.length, extListLoading]);

  const currentDriver = useMemo(
    () => drivers.find((d) => d.id === pickedDriverId) || null,
    [drivers, pickedDriverId],
  );

  // Fetch a wage suggestion whenever inputs that affect it change.
  //  • Fleet tab → /admin/driver-wage-rules/suggest (uses driver_id)
  //  • External tab + picked driver → /admin/external-driver-wage-rules/suggest
  //  • Otherwise → clear suggestion (no auto-fill).
  useEffect(() => {
    const bi = bookingInfo || booking;
    if (!bi) return;
    const pickup = bi.pickup_location || "";
    const destination = bi.destination || "";
    const carType = bi.car_type || "";
    const tripDate = bi.trip_date || "";
    if (!pickup || !destination || !carType) {
      setSuggestion(null);
      return;
    }

    const isExternalPick = tab === TAB_EXTERNAL && extMode === "pick" && extPickedId;
    const isFleetPick = tab === TAB_FLEET;
    if (!isExternalPick && !isFleetPick) {
      // External + manual mode → no per-driver suggestion.
      setSuggestion(null);
      return;
    }

    const params = new URLSearchParams({ pickup, destination, car_type: carType });
    if (tripDate) params.set("trip_date", tripDate);
    if (bi.id) params.set("booking_id", bi.id);

    let url;
    if (isExternalPick) {
      params.set("external_driver_id", extPickedId);
      url = `/admin/external-driver-wage-rules/suggest?${params.toString()}`;
    } else {
      if (pickedDriverId) params.set("driver_id", pickedDriverId);
      url = `/admin/driver-wage-rules/suggest?${params.toString()}`;
    }

    let cancelled = false;
    setSuggestLoading(true);
    api.get(url)
      .then((r) => { if (!cancelled) setSuggestion(r.data || null); })
      .catch(() => { if (!cancelled) setSuggestion(null); })
      .finally(() => { if (!cancelled) setSuggestLoading(false); });
    return () => { cancelled = true; };
  }, [bookingInfo, booking, pickedDriverId, tab, extMode, extPickedId]);

  // Auto-fill the wage input when a valid suggestion arrives and the user
  // hasn't typed a wage yet. Respects the "current wage from server" value.
  useEffect(() => {
    if (!suggestion || !suggestion.matched) return;
    if (wageManuallyEdited) return;
    const currentWage = Number(wage || 0);
    if (currentWage > 0) return; // don't overwrite an existing value
    const amt = Number(suggestion.wage_amount || 0);
    if (amt > 0) setWage(String(amt));
  }, [suggestion, wage, wageManuallyEdited]);

  // Wrapper so we can mark "manual edit" when the user types.
  const onWageChange = (v) => {
    setWageManuallyEdited(true);
    setWage(v);
  };

  // Pick a driver — busy ones open the override confirm first.
  const selectDriver = (d) => {
    if (d.is_booked) {
      setOverrideFor(d);
    } else {
      setAllowDouble(false);
      setPickedDriverId(d.id);
    }
  };
  const confirmOverride = () => {
    if (!overrideFor) return;
    setPickedDriverId(overrideFor.id);
    setAllowDouble(true);
    setOverrideFor(null);
  };

  // Per-row WhatsApp — sends a "possible assignment" preview message.
  const rowWhatsApp = (d, e) => {
    e.stopPropagation();
    const phone = formatPhoneForWhatsApp(d.phone);
    if (!phone) return toast.error("لا يوجد رقم واتساب لهذا السائق");
    const msg = buildDriverAssignmentMessage(bookingInfo || booking, d, { capacityMap: carsCapacity });
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(`فتحت رسالة واتساب لـ ${d.name}`);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { driver_wage: Number(wage || 0) };
      if (tab === TAB_FLEET) {
        if (!pickedDriverId) {
          toast.error("اختر سائقاً من القائمة");
          setSaving(false);
          return;
        }
        body.driver_id = pickedDriverId;
        if (allowDouble) body.allow_double_booking = true;
      } else {
        if (!extName.trim()) {
          toast.error("أدخل اسم السائق الخارجي");
          setSaving(false);
          return;
        }
        if (extPhone && String(extPhone).replace(/\D/g, "").length > 3) {
          const pcheck = validatePhone(extPhone);
          if (!pcheck.ok) {
            toast.error(pcheck.reason);
            setSaving(false);
            return;
          }
        }
        body.external_driver_name = extName.trim();
        body.external_driver_phone = extPhone.trim();
        // Link to the registered driver record when the "pick" mode was used —
        // required by the backend so wage-rule suggestions + round-trip
        // detection can filter by driver id.
        body.external_driver_id = extMode === "pick" ? (extPickedId || null) : null;
      }

      // Determine which booking ids to assign to.
      // Bulk-assign: apply to every sibling in the group (same driver + wage).
      // Per-route (2026-02): when the caller opened the modal from a per-route
      // row (booking._assignRouteIdx set), we assign to THAT route only and
      // never bulk-assign. Each leg of a multi-route booking can now have its
      // own driver — the admin decides who takes which route.
      const perRouteIdx = booking._assignRouteIdx;
      const perCarIdx = booking._assignCarIdx;
      const isPerRoute = perRouteIdx !== undefined && perRouteIdx !== null;
      const isPerCar = perCarIdx !== undefined && perCarIdx !== null;
      const targetIds = (hasGroup && assignToGroup && !isPerRoute && !isPerCar)
        ? siblings.map((s) => s.id)
        : [booking.id];
      if (isPerRoute) body.route_idx = perRouteIdx;
      if (isPerCar) body.car_idx = perCarIdx;

      // The allow_double_booking guard on the backend blocks conflicts; for a
      // group we intentionally pass allow_double_booking=true after the first
      // assignment so the same driver can cover sibling cars in the same slot.
      let updated = null;
      for (let i = 0; i < targetIds.length; i++) {
        const perCall = { ...body };
        if (hasGroup && assignToGroup && i > 0) perCall.allow_double_booking = true;
        try {
          const { data } = await api.post(`/bookings/${targetIds[i]}/assign`, perCall);
          if (i === 0) updated = data;
        } catch (err) {
          const detail = err?.response?.data?.detail || "تعذر الحفظ";
          toast.error(`الحجز ${i + 1}/${targetIds.length}: ${detail}`);
          throw err;
        }
      }

      const driverForMsg =
        tab === TAB_FLEET
          ? drivers.find((d) => d.id === pickedDriverId)
          : { name: extName.trim(), phone: extPhone.trim() };

      // Send ONE WhatsApp message even for bulk (driver only needs one).
      let opened = false;
      if (driverForMsg?.phone) {
        opened = openDriverWhatsApp(updated || booking, driverForMsg, { isExternal: tab !== TAB_FLEET, capacityMap: carsCapacity });
      }

      const count = targetIds.length;
      toast.success(
        driverForMsg?.phone
          ? `تم تعيين ${driverForMsg.name} على ${count > 1 ? `${count} سيارات` : "السيارة"}${opened ? " + فتحت رسالة واتساب" : ""}`
          : `تم تعيين السائق على ${count > 1 ? `${count} سيارات` : "السيارة"} (لا يوجد رقم جوال لفتح واتساب)`,
      );
      onSaved && onSaved(updated);
      onClose && onClose();
    } catch (e) {
      if (!e?._handled) {
        toast.error(e?.response?.data?.detail || "تعذر الحفظ");
      }
    } finally {
      setSaving(false);
    }
  };

  const iso = bookingInfo
    ? bookingInfo.pickup_time || combineDateTime(bookingInfo.trip_date, bookingInfo.trip_time)
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="driver-assign-modal"
    >
      <div
        className="w-full max-w-2xl bg-[#0F1F42] border border-primary/25 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
      >
        {/* Header */}
        <div className="p-4 border-b border-primary/15 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-white font-bold text-lg">تعيين سائق للمسار</h3>
            {bookingInfo && (
              <div className="text-xs text-white/60 mt-1 flex items-center gap-2 flex-wrap">
                <span className="text-primary num">#{bookingInfo.booking_number}</span>
                <span>·</span>
                <span>{bookingInfo.pickup_location}</span>
                <ArrowLeft size={10} />
                <span>{bookingInfo.destination}</span>
                <span>·</span>
                <span className="num">
                  {iso ? `${fmtDate(iso)} ${fmtTime(iso)}` : bookingInfo.trip_date}
                </span>
                <span>·</span>
                <span className="text-primary">{bookingInfo.car_type_name || bookingInfo.car_type}</span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/5 text-white/70"
            aria-label="إغلاق"
            data-testid="assign-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-primary/15 shrink-0">
          <button
            type="button"
            onClick={() => setTab(TAB_FLEET)}
            data-testid="tab-fleet"
            className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
              tab === TAB_FLEET
                ? "text-primary border-b-2 border-primary bg-primary/5"
                : "text-white/60 hover:text-white/80"
            }`}
          >
            <User size={16} /> سائق مسجل
          </button>
          <button
            type="button"
            onClick={() => setTab(TAB_EXTERNAL)}
            data-testid="tab-external"
            className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
              tab === TAB_EXTERNAL
                ? "text-primary border-b-2 border-primary bg-primary/5"
                : "text-white/60 hover:text-white/80"
            }`}
          >
            <UserPlus size={16} /> سائق خارجي
          </button>
        </div>

        {/* Body */}
        <div className="p-4 flex-1 overflow-y-auto min-h-0">
          {loading ? (
            <div className="text-center py-10 text-primary animate-pulse">جارٍ التحميل…</div>
          ) : tab === TAB_FLEET ? (
            drivers.length === 0 ? (
              <div className="text-center py-10 text-white/50 text-sm">
                لا يوجد سائقون متاحون
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-[11px] text-white/50 mb-1 leading-relaxed" data-testid="assign-cross-car-hint">
                  💡 يمكنك اختيار أي سائق (حتى من نوع سيارة مختلف). السائقون المطابقون لنوع سيارة الحجز يظهرون في الأعلى.
                </div>
                {drivers.map((d) => {
                  const picked = d.id === pickedDriverId;
                  const busy = d.is_booked;
                  const matches = d.matches_slot_car !== false; // default true if backend didn't send
                  return (
                    <div
                      key={d.id}
                      data-testid={`driver-row-${d.id}`}
                      className={`px-3 py-3 rounded-xl border transition-all flex items-center justify-between gap-2 cursor-pointer ${
                        busy && picked
                          ? "bg-[#F57F17]/15 border-[#F57F17] shadow-[0_0_0_1px_#F57F17]"
                          : busy
                          ? "bg-[#7F1D1D]/10 border-[#DC2626]/40 hover:border-[#DC2626]"
                          : picked
                          ? "bg-primary/20 border-primary shadow-[0_0_0_1px_var(--primary)]"
                          : "bg-[#0B1E4F]/40 border-primary/20 hover:border-primary/60"
                      }`}
                      onClick={() => selectDriver(d)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {busy ? (
                            <WarningCircle size={16} weight="fill" className="text-[#DC2626] shrink-0" />
                          ) : (
                            <CheckCircle size={16} weight="fill" className="text-[#22C55E] shrink-0" />
                          )}
                          <span className="text-white font-semibold truncate">{d.name}</span>
                          {d.primary_car_name_ar && (
                            <span
                              data-testid={`driver-car-badge-${d.id}`}
                              title={matches ? "سيارة السائق تطابق نوع الحجز" : "تعيين متقاطع — نوع سيارة السائق يختلف عن حجز العميل"}
                              className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full border font-bold shrink-0 ${
                                matches
                                  ? "bg-primary/15 border-primary/50 text-primary"
                                  : "bg-sky-500/15 border-sky-400/60 text-sky-300"
                              }`}
                            >
                              🚗 {d.primary_car_name_ar}
                            </span>
                          )}
                          {d.is_current && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/30 text-primary">
                              الحالي
                            </span>
                          )}
                          {busy && picked && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F57F17]/20 border border-[#F57F17]/50 text-[#F57F17] font-bold">
                              رحلة إضافية
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-white/50 mt-1 num" dir="ltr">
                          {d.phone ? formatPhoneDisplay(d.phone) : "—"}
                          {d.car_plate && <span className="mr-2 text-primary/70">| {d.car_plate}</span>}
                        </div>
                        {busy && d.conflict && (
                          <div className="text-[11px] text-[#FCA5A5] mt-1">
                            🔴 مشغول: حجز <span className="num">#{d.conflict.booking_number}</span>
                            {d.conflict.trip_time && (
                              <> — <span className="num">{d.conflict.trip_time}</span></>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="shrink-0 flex items-center gap-2">
                        {d.phone && (
                          <button
                            type="button"
                            onClick={(e) => rowWhatsApp(d, e)}
                            data-testid={`driver-wa-${d.id}`}
                            title="إرسال واتساب"
                            className="w-9 h-9 rounded-lg bg-[#25D366] hover:bg-[#1EBE57] text-white flex items-center justify-center transition-transform hover:scale-105 shrink-0"
                          >
                            <WhatsappLogo size={16} weight="fill" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); selectDriver(d); }}
                          data-testid={`driver-pick-${d.id}`}
                          className={`px-4 py-2 rounded-lg text-xs font-bold transition-all border ${
                            picked
                              ? "bg-primary text-primary-foreground border-primary"
                              : busy
                                ? "bg-[#DC2626]/15 text-[#F87171] border-[#DC2626]/60 hover:bg-[#DC2626] hover:text-white"
                                : "bg-primary/10 text-primary border-primary/40 hover:bg-primary hover:text-primary-foreground"
                          }`}
                        >
                          {picked ? "✓ مختار" : busy ? "⚠️ اختر" : "اختر"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            <div className="space-y-3">
              {/* Mode toggle — pick from registered list vs manual entry */}
              <div className="grid grid-cols-2 gap-2" data-testid="ext-mode-toggle">
                <button
                  type="button"
                  onClick={() => setExtMode("pick")}
                  data-testid="ext-mode-pick"
                  className={`py-2.5 rounded-lg text-xs font-bold transition-all border ${
                    extMode === "pick"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-primary/5 border-primary/25 text-white/70 hover:bg-primary/10"
                  }`}
                >
                  📋 اختر من القائمة
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setExtMode("manual");
                    setExtPickedId(null);
                    setSuggestion(null);
                  }}
                  data-testid="ext-mode-manual"
                  className={`py-2.5 rounded-lg text-xs font-bold transition-all border ${
                    extMode === "manual"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-primary/5 border-primary/25 text-white/70 hover:bg-primary/10"
                  }`}
                >
                  ✍️ إدخال يدوي
                </button>
              </div>

              {extMode === "pick" ? (
                extListLoading ? (
                  <div className="text-center py-8 text-primary text-xs animate-pulse">جارٍ التحميل…</div>
                ) : extList.length === 0 ? (
                  <div className="text-center py-6 text-white/60 text-sm bg-primary/5 border border-primary/20 rounded-xl space-y-2" data-testid="ext-empty">
                    <div>لا يوجد سائقون خارجيون مسجّلون بعد.</div>
                    <Link
                      to="/admin/external-drivers"
                      target="_blank"
                      data-testid="ext-goto-manage"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-primary/20 border border-primary/40 text-primary hover:bg-primary/30"
                    >
                      <Plus size={12} weight="bold" /> إضافة سائق خارجي
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="ext-list">
                    {extList.map((d) => {
                      const active = extPickedId === d.id;
                      const digits = String(d.phone || "").replace(/\D/g, "");
                      return (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => {
                            setExtPickedId(d.id);
                            setExtName(d.name || "");
                            setExtPhone(d.phone || "");
                            // Clear a manually-typed wage so the picker's own
                            // suggestion re-populates.
                            setWageManuallyEdited(false);
                            setWage("");
                          }}
                          data-testid={`ext-pick-${d.id}`}
                          className={`w-full px-3 py-2.5 rounded-xl border transition-all flex items-center justify-between gap-2 text-right ${
                            active
                              ? "bg-primary/20 border-primary shadow-[0_0_0_1px_var(--primary)]"
                              : "bg-[#0B1E4F]/40 border-primary/20 hover:border-primary/60"
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-white font-semibold truncate">{d.name}</span>
                              {d.base_city && (
                                <span
                                  className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/15 border border-primary/40 text-primary font-bold shrink-0"
                                  data-testid={`ext-city-${d.id}`}
                                >
                                  {d.base_city === "makkah" ? "🕌 مكة" : "🌙 المدينة"}
                                </span>
                              )}
                              {active && (
                                <CheckCircle size={14} weight="fill" className="text-primary shrink-0" />
                              )}
                            </div>
                            {digits && (
                              <div className="text-xs text-white/50 mt-1 num" dir="ltr">
                                {formatPhoneDisplay(d.phone)}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )
              ) : (
                <>
                  <div>
                    <label className="block text-xs text-white/60 mb-1">الاسم</label>
                    <input
                      type="text"
                      value={extName}
                      onChange={(e) => setExtName(e.target.value)}
                      className="mz-input"
                      placeholder="اسم السائق الخارجي"
                      data-testid="ext-name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/60 mb-1">رقم الجوال</label>
                    <PhoneInput
                      value={extPhone}
                      onChange={setExtPhone}
                      testId="ext-phone"
                      placeholder="اختر الدولة ثم أدخل الرقم"
                    />
                  </div>
                </>
              )}

              <div className="text-xs text-[#25D366] bg-[#25D366]/10 border border-[#25D366]/30 rounded-lg p-2 inline-flex items-center gap-1.5">
                <WhatsappLogo size={13} weight="fill" /> سيتم فتح رسالة واتساب تلقائياً بعد الحفظ.
              </div>
            </div>
          )}
        </div>

        {/* Wage + Actions */}
        <div className="p-4 border-t border-primary/15 space-y-3 shrink-0">
          <WageSuggestionCard
            suggestion={suggestion}
            loading={suggestLoading}
            bookingCarType={bookingInfo?.car_type || booking?.car_type}
            bookingCarName={bookingInfo?.car_type_name || bookingInfo?.car_type || booking?.car_type_name || booking?.car_type}
            carsLabels={carsLabels}
            driverPicked={
              !!pickedDriverId ||
              (tab === TAB_EXTERNAL && extMode === "pick" && !!extPickedId) ||
              (tab === TAB_EXTERNAL && extMode === "manual" && extName.trim().length > 0)
            }
            isExternal={tab === TAB_EXTERNAL}
            onApply={() => {
              const amt = Number(suggestion?.wage_amount || 0);
              if (amt > 0) {
                setWage(String(amt));
                setWageManuallyEdited(false);
              }
            }}
          />
          <div>
            <label className="block text-xs text-white/60 mb-1">أجرة السائق (ر.س)</label>
            <NumberField
              value={wage}
              onChange={onWageChange}
              className="mz-input num"
              placeholder="0"
              min={0}
              testId="assign-wage"
            />
          </div>
          {tab === TAB_FLEET && currentDriver && (
            <div className={`text-xs rounded-lg p-2 border ${
              allowDouble
                ? "bg-[#F57F17]/10 border-[#F57F17]/40 text-[#F57F17]"
                : "bg-primary/5 border-primary/15 text-white/60"
            }`}>
              {allowDouble && <span className="font-bold">⚠️ رحلة إضافية · </span>}
              سيتم تعيين <span className="text-primary font-bold">{currentDriver.name}</span> بأجرة{" "}
              <span className="num text-primary">{fmtNum(wage || 0)}</span> ر.س
            </div>
          )}
          {hasGroup && (
            <label
              className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 p-2.5 cursor-pointer hover:bg-primary/10"
              data-testid="assign-to-group-toggle"
            >
              <input
                type="checkbox"
                checked={assignToGroup}
                onChange={(e) => setAssignToGroup(e.target.checked)}
                className="w-4 h-4 accent-[#C9A961]"
              />
              <span className="text-xs text-white/85 leading-tight">
                تعيين نفس السائق لكل السيارات في هذا الطلب{" "}
                <span className="text-primary font-bold num">
                  (× {fmtNum(siblings.length)})
                </span>
              </span>
            </label>
          )}
          <div className="flex items-center gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-full text-sm border border-white/15 text-white/70 hover:bg-white/5"
              data-testid="assign-cancel"
            >
              إلغاء
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || loading}
              className="px-6 py-2 rounded-full text-sm font-bold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              data-testid="assign-save"
            >
              {saving ? "جارٍ الحفظ…" : "حفظ"}
            </button>
          </div>
        </div>
      </div>

      <OverrideConfirm
        driver={overrideFor}
        booking={bookingInfo || booking}
        onCancel={() => setOverrideFor(null)}
        onConfirm={confirmOverride}
      />
    </div>
  );
}


// ─── Wage Suggestion Card ──────────────────────────────────────────────
// Shown right above the "أجرة السائق" input. Three states:
//   1. no rule matched → yellow warning + "add rule" button
//   2. round-trip pattern detected → blue card + "apply"
//   3. one-way → gold card + "apply"
// The parent already auto-fills the wage input when the input is empty; the
// "apply" button here forces overwriting a manually-typed value.
function WageSuggestionCard({
  suggestion,
  loading,
  driverPicked,
  isExternal = false,
  bookingCarType,
  bookingCarName,
  carsLabels = {},
  onApply,
}) {
  if (loading) {
    return (
      <div
        data-testid="wage-suggestion-loading"
        className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-white/60 text-xs inline-flex items-center gap-1.5"
      >
        <ArrowsClockwise size={11} weight="bold" className="animate-spin" />
        جاري حساب الأجرة المقترحة…
      </div>
    );
  }
  if (!suggestion) return null;

  const rulesLink = isExternal ? "/admin/external-driver-wage-rules" : "/admin/driver-wage-rules";

  // No matching rule.
  if (!suggestion.matched) {
    return (
      <div
        data-testid="wage-suggestion-no-rule"
        className="rounded-lg border border-[#F59E0B]/50 bg-[#F59E0B]/10 p-3 space-y-2"
      >
        <div className="flex items-start gap-2">
          <WarningCircle size={16} weight="fill" className="text-[#F59E0B] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-[#F59E0B] font-bold text-sm">لا توجد قاعدة أجرة مطابقة</div>
            <div className="text-white/70 text-[11px] mt-0.5 leading-relaxed">
              {suggestion.message || "لم نجد قاعدة تسعيرة لهذا المسار ونوع السيارة"}
            </div>
          </div>
        </div>
        <Link
          to={rulesLink}
          data-testid="wage-suggestion-add-rule"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-[#F59E0B]/20 border border-[#F59E0B]/50 text-[#F59E0B] hover:bg-[#F59E0B]/30 transition-colors"
        >
          <Plus size={11} weight="bold" />
          أضف قاعدة جديدة
        </Link>
      </div>
    );
  }

  const isRT = !!suggestion.pattern?.is_round_trip;
  const amount = Number(suggestion.wage_amount || 0);
  const styles = isRT
    ? {
        border: "border-[#3B82F6]/50",
        bg: "bg-[#3B82F6]/10",
        pill: "bg-[#3B82F6]/20 border-[#3B82F6]/50 text-[#93C5FD]",
        title: "text-[#93C5FD]",
        icon: <ArrowsClockwise size={16} weight="fill" className="text-[#3B82F6]" />,
        label: "حركة كاملة (ذهاب وعودة)",
      }
    : {
        border: "border-primary/50",
        bg: "bg-primary/10",
        pill: "bg-primary/20 border-primary/50 text-primary",
        title: "text-primary",
        icon: <ArrowRight size={16} weight="fill" className="text-primary" />,
        label: "حركة فردية (اتجاه واحد)",
      };

  return (
    <div
      data-testid="wage-suggestion-card"
      className={`rounded-lg border ${styles.border} ${styles.bg} p-3 space-y-2`}
    >
      <div className="flex items-start gap-2">
        {styles.icon}
        <div className="flex-1 min-w-0">
          <div className={`font-bold text-sm ${styles.title}`} data-testid="wage-suggestion-type">
            {styles.label}
          </div>
          <div className="flex items-baseline gap-1.5 mt-1">
            <span className="num text-white font-black text-xl" dir="ltr" data-testid="wage-suggestion-amount">
              {fmtNum(amount)}
            </span>
            <span className="text-white/60 text-xs">ر.س</span>
          </div>
          {suggestion.matched_rule && (
            <div className="text-white/50 text-[11px] mt-1 truncate">
              {suggestion.matched_rule.pickup_location}
              <ArrowLeft size={9} className="inline mx-1" />
              {suggestion.matched_rule.destination}
            </div>
          )}
          {suggestion.matched_rule &&
            bookingCarType &&
            suggestion.matched_rule.car_type &&
            suggestion.matched_rule.car_type !== bookingCarType && (
              <div
                data-testid="wage-suggestion-car-mismatch"
                title="القاعدة مسجّلة لنوع سيارة مختلف — تم المطابقة بالمدن فقط"
                className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border border-sky-400/60 bg-sky-500/15 text-sky-300"
              >
                🔀 قاعدة {carsLabels[suggestion.matched_rule.car_type] || suggestion.matched_rule.car_type}
                <span className="text-sky-200/70">·</span>
                <span>مطبّقة على {carsLabels[bookingCarType] || bookingCarName || bookingCarType}</span>
              </div>
            )}
          {suggestion.pattern?.reason && (
            <div className="text-white/60 text-[11px] mt-1.5 leading-relaxed flex items-start gap-1">
              <Info size={10} weight="bold" className="text-white/40 flex-shrink-0 mt-0.5" />
              <span>{suggestion.pattern.reason}</span>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <button
          type="button"
          onClick={onApply}
          data-testid="wage-suggestion-apply"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold border transition-colors ${styles.pill}`}
        >
          <CheckCircle size={11} weight="bold" />
          تطبيق {fmtNum(amount)} ر.س
        </button>
        {suggestion.pattern?.paired_booking_id && (
          <Link
            to={`/admin/bookings/${suggestion.pattern.paired_booking_id}`}
            target="_blank"
            data-testid="wage-suggestion-paired-link"
            className="inline-flex items-center gap-1 text-[11px] text-white/60 hover:text-white underline decoration-white/30 hover:decoration-white"
          >
            عرض الرحلة المقابلة
          </Link>
        )}
      </div>
    </div>
  );
}
